import { forwardRef, useCallback, type ReactNode } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { Message } from '@components/templates/bia/hooks/useStreamState';
import '@components/templates/bia/chat/markdown.css';
import { clsx } from 'clsx';
import { chatLinkTarget } from './chatLinkTarget';
import { openChatModalInPlace } from './chatModalUrl';

interface ChatMessageComponentProps {
  message: Message;
  // Fired when a CV detail link is opened, so the caller can seed the talent
  // modal's navigation list from this message's results.
  onInteraction?: (message: Message) => void;
}

const formatContent = (content: string): string[] => {
  let formattedContent = content.replace(/<br>/g, '\n\n');
  formattedContent = formattedContent.replace(/(\n)###/g, '\n\n###');
  return [formattedContent];
};

export const ChatMessageComponent = forwardRef<
HTMLDivElement,
ChatMessageComponentProps
>((props, ref) => {
  const {
    message,
    onInteraction,
  } = props;
  const { type, content } = message;

  const isUserMessage = type === 'human';

  const formattedContent = !isUserMessage ? formatContent(content) : [content];

  const markdownLink = useCallback(({ href, children }: { href?: string; children?: ReactNode }) => {
    const target = chatLinkTarget(href);

    if (target.kind === 'plain') {
      return <span>{children}</span>;
    }

    if (target.kind === 'job' || target.kind === 'cv') {
      return (
        <a
          className="font-medium text-primary underline underline-offset-2"
          href={href}
          onClick={(event) => {
            // Let the browser handle modified clicks — ctrl/cmd/shift and middle-click must still
            // open a real tab, the way the job cards below already do.
            if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
            event.preventDefault();
            // CV links only: the callback seeds the talent modal's navigation list.
            if (target.kind === 'cv') onInteraction?.(message);
            openChatModalInPlace(target.kind === 'job' ? 'selectedJobSlug' : 'selectedCvSlug', target.slug);
          }}
        >
          {children}
        </a>
      );
    }

    // Anything without a modal opens in a new tab, so the chat thread survives.
    return (
      <a
        className="font-medium text-primary underline underline-offset-2"
        href={target.href}
        target="_blank"
        rel="noopener noreferrer"
      >
        {children}
      </a>
    );
  }, [message, onInteraction]);

  return (
    <>
      {formattedContent.join('').trim() !== '' && (
        <div
          ref={ref}
          className={clsx(
            'flex flex-col gap-5',
            isUserMessage ? 'items-end' : 'items-start',
          )}
        >

          <div
            className={clsx(
              'markdown rounded-2xl px-4 py-2 text-sm',
              isUserMessage ? 'rounded-tl-2xl rounded-tr-sm bg-ink text-surface' : 'rounded-tl-sm rounded-tr-2xl bg-surface',
            )}
          >
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeRaw]}
              components={{ a: markdownLink }}
            >
              {formattedContent.join('')}
            </ReactMarkdown>
          </div>
        </div>
      )}
    </>
  );
});

ChatMessageComponent.displayName = 'ChatMessageBubbleAI';
